// Sample URLs:
// https://www.sonder.com/destinations/chicago/search?check_in_dt=2019-08-28&check_out_dt=2019-09-25&sleeps=3
// https://www.sonder.com/destinations/new_york_city/search?check_in_dt=2019-08-28&check_out_dt=2019-09-17&sleeps=1

import isNumber from "lodash/isNumber";
import snakeCase from "lodash/snakeCase";
import urljoin from "url-join";

import i18n, { LANGUAGES, LANGUAGES2 } from "components/i18n/source";
import { citiesData } from "components/pages/home/content/cities";
import * as urlUtils from "components/pages/search/utils/urlUtils";

import { toSnakeCase } from "./format";

const toLegacyKey = name => {
  const mapping = {
    destination: "destination",
    guests: "sleeps",
    checkIn: "check_in_dt",
    checkOut: "check_out_dt",
    neighborhood: "neighborhood",
    geoBounds: "geo_bounds",
    geoDistance: "geo_distance",
    buildingId: "building_id",
    collectionId: "collection_id",
  };

  return mapping[name];
};

const toLegacyValue = (name, value) => {
  const getNeighborhoodValue = neighborhood => {
    if (typeof neighborhood === "object") {
      return neighborhood.value;
    }

    return neighborhood;
  };

  const mapping = {
    guests: guestObj => Object.values(guestObj).reduce((sum, x) => sum + x, 0),
    checkIn: checkIn => checkIn,
    checkOut: checkOut => checkOut,
    neighborhood: neighborhood => `${getNeighborhoodValue(neighborhood)}`,
    geoBounds: geoBounds =>
      `${[
        geoBounds.ne.lat,
        geoBounds.ne.lng,
        geoBounds.sw.lat,
        geoBounds.sw.lng,
      ]}`,
    geoDistance: geoDistance =>
      `${[
        geoDistance.center.lat,
        geoDistance.center.lng,
        geoDistance.distance,
      ]}`,
    buildingId: buildingId => buildingId,
    collectionId: collectionId => collectionId,
  };

  return mapping[name](value);
};

const convertToLegacy = params => {
  return Object.keys(params).reduce((result, param) => {
    const key = toLegacyKey(param);
    const value = toLegacyValue(param, params[param]);
    return {
      ...result,
      [key]: value,
    };
  }, {});
};

const getUtmParams = params => {
  if (!params) return {};

  const utmParams = Object.keys(params).reduce((result, paramName) => {
    if (paramName.startsWith("utm")) {
      return {
        ...result,
        [snakeCase(paramName)]: params[paramName],
      };
    }

    return result;
  }, {});

  return utmParams;
};

const isAbsolutePath = href => {
  return (
    href.startsWith("http://") ||
    href.startsWith("https://") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:") ||
    href.startsWith("sms:")
  );
};

export const buildSearchQueryString = (params = {}) => {
  const {
    amenities,
    guests,
    checkIn,
    checkOut,
    neighborhood,
    geoBounds,
    geoDistance,
    priceMin,
    priceMax,
    urlParams,
    sortBy,
    buildingId,
  } = params;

  const isPriceRangeProvided = isNumber(priceMin) && isNumber(priceMax);
  const amenitiesString = urlUtils.amenitiesToParam(amenities).amenities;
  // Make sure to keep existing params like utmParams
  const utmParams = getUtmParams(urlParams);

  const queryParams = {
    ...(guests && { guests }),
    ...(checkIn && { checkIn }),
    ...(checkOut && { checkOut }),
    ...(neighborhood && { neighborhood }),
    ...(geoBounds && { geoBounds }),
    ...(geoDistance && { geoDistance }),
    ...(buildingId && { buildingId }),
  };

  const paramString = new URLSearchParams({
    // TODO: move the legacy conversion logic into individual urlUtils functions
    ...convertToLegacy(queryParams),
    ...(amenitiesString &&
      amenitiesString.length && { amenities: `${amenitiesString}` }),
    ...(isPriceRangeProvided && urlUtils.pricesToParam({ priceMin, priceMax })),
    ...urlUtils.countersToParam(amenities),
    ...urlUtils.sortByToParam(sortBy),
    ...utmParams,
  });

  return paramString;
};

export const buildSearchPath = (params = {}) => {
  const city = toSnakeCase(params.destination);
  if (!Object.keys(citiesData).includes(city)) {
    // invalid city name; return to the home page
    return localizeHref("/");
  }
  const paramString = buildSearchQueryString(params);

  return `/destinations/${city}/search?${paramString}`;
};

export const updateSearchPath = path => {
  if (typeof window !== "undefined") {
    window.history.replaceState(null, "", localizeHref(path));
  }
};

export const localizeHref = href => {
  if (href === null) return href;
  if (i18n.language === "en-US") return href;

  // Anchor link for the same page
  if (href.startsWith("#")) return href;

  // [Absolute paths]
  // Absolute paths will not be decorated with locale
  if (isAbsolutePath(href)) return href;

  // [Relative paths]
  // if the href is relative, we need to decorate it with the
  // locale unless the locale is english
  // Added "/" to end, as there might be an url component starts with locale code
  const prefix = urljoin("/", i18n.language.toLowerCase(), "/");

  if (href.startsWith(prefix)) return href;

  // Clean up locale part if exists
  // This part is to convert locales between legacy and new like (fr-fr <-> fr)
  // TODO: Remove this part when we release new locales to 100% and update corresponding flatbook code
  let hrefToUpdate = href;
  const urlComponents = href?.split("/");
  if (urlComponents && urlComponents.length > 1) {
    const firstUrlComponent = urlComponents[1].toLowerCase();
    const languageList = [...LANGUAGES, ...LANGUAGES2];
    const locale = languageList.find(
      lang => lang.value.toLowerCase() === firstUrlComponent,
    );

    if (locale) {
      const prefixToClear = urljoin("/", urlComponents[1]);
      hrefToUpdate = hrefToUpdate.replace(prefixToClear, "");
    }
  }

  return urljoin(prefix, hrefToUpdate);
};

export const navigateTo = path => {
  path = localizeHref(path);
  global.location.assign(path || "/");
};

export const privacyPolicyPath = () => localizeHref("/privacy-policy");

export const buildPropertyListingQuery = (params = {}, urlParams = {}) => {
  const { guests, checkIn, checkOut, priceMin, priceMax } = params;

  const isPriceRangeProvided = isNumber(priceMin) && isNumber(priceMax);
  // Make sure to keep existing params like utmParams
  const utmParams = getUtmParams(urlParams);

  const queryParams = {
    ...(urlParams?.collectionId
      ? { collectionId: urlParams.collectionId }
      : {}),
    ...(guests && { guests }),
    ...(checkIn && { checkIn }),
    ...(checkOut && { checkOut }),
  };
  const paramString = new URLSearchParams({
    // TODO: move the legacy conversion logic into individual urlUtils functions
    ...convertToLegacy(queryParams),
    ...(isPriceRangeProvided && urlUtils.pricesToParam({ priceMin, priceMax })),
    ...utmParams,
  });

  return paramString;
};

export const buildPropertyListingPath = (params = {}, urlParams = {}) => {
  const paramString = buildPropertyListingQuery(params, urlParams);
  return `/destinations/${urlParams?.city}/${urlParams?.adName}/${urlParams?.id}?${paramString}`;
};
